/**
 * グループ 1 件のカード。クリックでインライン展開、メンバー一覧 + 操作。
 * 仕様書: docs/01_spec/spec_mail_system.md B-11.3
 */
import { ChevronDown, ChevronRight, Sparkles, Trash2, UserMinus, UserPlus, Users } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import { useContactsInGroup } from '@/hooks/useMailContacts';
import {
  useDeleteMailGroup,
  useRemoveMemberFromGroup,
  type MailGroupRecord,
} from '@/hooks/useMailGroups';
import { formatDate } from '@/lib/firestoreTime';

import { MemberPickerDialog } from './MemberPickerDialog';

interface Props {
  group: MailGroupRecord;
}

export function GroupCard({ group }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  // 展開時のみメンバーを取得（mail_contacts.groupIds で正確に絞り込み）
  const { data: members = [] } = useContactsInGroup(expanded ? group.id : null);
  const deleteMutation = useDeleteMailGroup();
  const removeMember = useRemoveMemberFromGroup();

  // group 側の memberContactIds とのカウント差（双方向同期が破綻していないかの diagnostic）
  const groupSideCount = group.memberContactIds?.length ?? 0;
  const driftCount = expanded ? Math.abs(groupSideCount - members.length) : 0;

  const handleDeleteGroup = async () => {
    if (
      !window.confirm(
        `グループ「${group.name}」を削除します。配信先 (mail_contacts) には影響しません。`,
      )
    )
      return;
    try {
      await deleteMutation.mutateAsync(group.id);
      toast.success('グループを削除しました');
    } catch (err) {
      toast.error(`削除失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleRemoveMember = async (contactId: string, label: string) => {
    if (!window.confirm(`「${label}」をこのグループから除外します。`)) return;
    try {
      await removeMember.mutateAsync({ groupId: group.id, contactId });
      toast.success('グループから除外しました');
    } catch (err) {
      toast.error(`除外失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <div className="rounded bg-[var(--bg-elevated)] border border-[var(--border)]">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-[var(--bg-hover)] transition-colors rounded-t"
      >
        {expanded ? (
          <ChevronDown className="size-4 text-[var(--text-muted)] flex-shrink-0" />
        ) : (
          <ChevronRight className="size-4 text-[var(--text-muted)] flex-shrink-0" />
        )}
        {group.type === 'ai_keyword' ? (
          <Sparkles className="size-4 text-[var(--accent-purple)] flex-shrink-0" />
        ) : (
          <Users className="size-4 text-[var(--accent)] flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-semibold truncate">{group.name}</span>
            <Badge variant={group.type === 'ai_keyword' ? 'accent' : 'default'}>
              {group.type === 'ai_keyword' ? 'AI' : '手動'}
            </Badge>
            <Badge variant="default">{group.memberContactIds?.length ?? 0} 名</Badge>
          </div>
          {group.description && (
            <div className="text-xs text-[var(--text-muted)] truncate mt-0.5">
              {group.description}
            </div>
          )}
        </div>
        <div className="text-[10px] text-[var(--text-muted)] flex-shrink-0">
          {formatDate(group.updatedAt)}
        </div>
      </button>

      {expanded && (
        <div className="px-3 pb-3 border-t border-[var(--border)] pt-3 space-y-3">
          {group.type === 'ai_keyword' && group.keywords && group.keywords.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">
                AI キーワード
              </div>
              <div className="flex flex-wrap gap-1">
                {group.keywords.map((k) => (
                  <Badge key={k} variant="accent">
                    {k}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                メンバー（{members.length} 名）
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => setPickerOpen(true)}>
                <UserPlus className="size-3 mr-1" />
                メンバー追加
              </Button>
            </div>
            {members.length === 0 ? (
              <div className="text-xs text-[var(--text-muted)] italic py-3 text-center">
                メンバー未登録
              </div>
            ) : (
              <div className="space-y-1 max-h-72 overflow-y-auto">
                {members.map((m) => (
                  <div
                    key={m.id}
                    className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[var(--bg-hover)] text-xs"
                  >
                    <div className="flex-1 min-w-0">
                      <span className="font-medium">{m.name || '（名前なし）'}</span>
                      <span className="text-[var(--text-muted)] ml-2 font-mono">
                        {m.defaultEmail}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        handleRemoveMember(m.id, m.name || m.defaultEmail)
                      }
                      className="text-[var(--text-muted)] hover:text-[var(--accent-red)] flex-shrink-0"
                      title="グループから除外"
                    >
                      <UserMinus className="size-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {driftCount > 0 && (
              <div className="text-[10px] text-[var(--accent-amber)] italic mt-1">
                ⚠ 双方向同期にズレ {driftCount} 件（owner に migrateGroupSync の実行を依頼してください）
              </div>
            )}
          </div>

          <div className="flex justify-end pt-2 border-t border-[var(--border)]">
            <Button type="button" variant="danger" size="sm" onClick={handleDeleteGroup}>
              <Trash2 className="size-3 mr-1" />
              グループ削除
            </Button>
          </div>
        </div>
      )}

      <MemberPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        groupId={group.id}
        existingMemberIds={group.memberContactIds ?? []}
      />
    </div>
  );
}
